// teamSchedulePdf.ts
// REWRITTEN: Full rewrite replacing jspdf-autotable table approach with a manual visual
// timeline renderer that mirrors the DayTimeline web component.
// Changes:
// - Visual timeline: hour grid lines + task blocks positioned by time (like the Team page)
// - Service color system fixed: color NAMES (blue, teal, orange…) mapped to RGB palettes
// - Logo embedded in header (white variant, fetched at runtime)
// - Header and footer drawn on every page independently
// - Task name displayed in bold for readability
// - Single-operator pages: column width capped (no full-page stretching)
// - Page overflow threshold tuned: fewer near-empty pages

import jsPDF from 'jspdf';

export interface ScheduleTask {
  id: string;
  title: string;
  startTime: string;
  duration: number;
  operatorId: string;
  serviceColor?: string;
  serviceName?: string;
}

export interface ScheduleOperator {
  id: string;
  first_name: string;
  last_name: string;
}

export interface GenerateSchedulePdfOptions {
  siteName: string;
  date: Date;
  displayedOperators: ScheduleOperator[];
  tasks: ScheduleTask[];
}

type RGB = [number, number, number];

// --- Service color palettes ---
// Maps color names (as stored in the DB) to RGB triplets matching Tailwind -100/-300/-800 shades
const SERVICE_PALETTES: Record<string, { bg: RGB; border: RGB; text: RGB }> = {
  blue:    { bg: [219, 234, 254], border: [147, 197, 253], text: [30,  64,  175] },
  cyan:    { bg: [207, 250, 254], border: [103, 232, 249], text: [21,  94,  117] },
  teal:    { bg: [204, 251, 241], border: [94,  234, 212], text: [17,  94,   89] },
  lime:    { bg: [236, 252, 203], border: [190, 242, 100], text: [63,  98,   18] },
  orange:  { bg: [255, 237, 213], border: [253, 186, 116], text: [154,  52,  18] },
  amber:   { bg: [254, 243, 199], border: [252, 211,  77], text: [146,  64,  14] },
  rose:    { bg: [255, 228, 230], border: [253, 164, 175], text: [159,  18,  57] },
  pink:    { bg: [252, 231, 243], border: [249, 168, 212], text: [157,  23,  77] },
  violet:  { bg: [237, 233, 254], border: [196, 181, 253], text: [91,   33, 182] },
  fuchsia: { bg: [250, 232, 255], border: [240, 171, 252], text: [134,  25, 143] },
  sky:     { bg: [224, 242, 254], border: [125, 211, 252], text: [7,    89, 133] },
  indigo:  { bg: [224, 231, 255], border: [165, 180, 252], text: [55,   48, 163] },
};

const FALLBACK_PALETTE = {
  bg:     [243, 244, 246] as RGB,
  border: [209, 213, 219] as RGB,
  text:   [31,   41,  55] as RGB,
};

function getServicePalette(colorName?: string) {
  if (!colorName) return FALLBACK_PALETTE;
  return SERVICE_PALETTES[colorName] ?? FALLBACK_PALETTE;
}

// --- Design system colors (matching app palette) ---
const C_HEADER_BG:     RGB = [26,  68,  60];  // primary-900
const C_HEADER_TEXT:   RGB = [255, 255, 255];  // white
const C_OP_HDR_BG:     RGB = [232, 245, 243];  // primary-50
const C_OP_HDR_TEXT:   RGB = [44,  104,  96];  // primary-700
const C_GRID_LINE:     RGB = [229, 231, 235];  // gray-200
const C_TIME_LABEL:    RGB = [62,  139, 130];  // primary-500
const C_META_TEXT:     RGB = [107, 114, 128];  // gray-500
const C_FOOTER_TEXT:   RGB = [156, 163, 175];  // gray-400
const C_FOOTER_LINE:   RGB = [203, 213, 225];  // slate-300

// --- Layout constants (mm) ---
const MARGIN         = 10;
const HEADER_H       = 14;
const META_H         = 5;
const COL_HEADER_H   = 8;
const FOOTER_H       = 8;
const TIME_COL_W     = 16;
const MIN_BLOCK_H    = 7;
const BLOCK_INSET    = 0.8;
const MIN_OP_COL_W   = 55;
const MAX_SINGLE_COL = 115;

// --- Utility functions ---

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
}

function minutesToTime(total: number): string {
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

function formatTimeShort(time: string): string {
  return time.substring(0, 5);
}

function formatDateFrench(date: Date): string {
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const months = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
  ];
  return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

function normalizeSiteSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[éèêë]/g, 'e').replace(/[àâä]/g, 'a').replace(/[ùûü]/g, 'u')
    .replace(/[îï]/g, 'i').replace(/[ôö]/g, 'o').replace(/ç/g, 'c')
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 40);
}

// --- Logo loader ---

async function loadLogo(url: string, timeoutMs = 5000): Promise<{ data: string; aspect: number } | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
      resolve(null);
    }, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL('image/png'), aspect: img.naturalWidth / img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });
}

// --- Task overlap layout ---

interface TaskLayout {
  task: ScheduleTask;
  column: number;
  totalColumns: number;
}

function calculateTaskLayouts(tasks: ScheduleTask[]): TaskLayout[] {
  const sorted = [...tasks].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
  const colEnds: number[] = [];
  const assignments: { task: ScheduleTask; col: number }[] = [];

  for (const task of sorted) {
    const start = timeToMinutes(task.startTime);
    const end   = start + task.duration;
    let col = 0;
    while (col < colEnds.length && colEnds[col] > start) col++;
    colEnds[col] = end;
    assignments.push({ task, col });
  }

  const totalCols = Math.max(1, colEnds.length);
  return assignments.map(({ task, col }) => ({ task, column: col, totalColumns: totalCols }));
}

// --- Page section drawers ---

function drawHeader(
  doc: jsPDF,
  x: number, y: number, w: number,
  siteName: string, formattedDate: string,
  logo: { data: string; aspect: number } | null,
) {
  doc.setFillColor(...C_HEADER_BG);
  doc.rect(x, y, w, HEADER_H, 'F');

  const logoH = 8;
  let textStartX = x + 5;

  if (logo) {
    const logoW  = Math.min(logoH * logo.aspect, 44);
    const logoY  = y + (HEADER_H - logoH) / 2;
    doc.addImage(logo.data, 'PNG', x + 4, logoY, logoW, logoH);
    textStartX = x + 4 + logoW + 5;
  }

  doc.setTextColor(...C_HEADER_TEXT);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Planning Equipe', textStartX, y + HEADER_H / 2 + 2.2);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`${siteName}  |  ${formattedDate}`, x + w - 3, y + HEADER_H / 2 + 2.2, { align: 'right' });
}

function drawMetaLine(
  doc: jsPDF,
  x: number, y: number, w: number,
  generatedAt: string, pageNum: number, totalPages: number,
) {
  doc.setTextColor(...C_META_TEXT);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`Généré le ${generatedAt}`, x, y + 3.5);
  doc.text(`Page ${pageNum} / ${totalPages}`, x + w, y + 3.5, { align: 'right' });
}

function drawColumnHeaders(
  doc: jsPDF,
  x: number, y: number,
  timeColW: number,
  operators: ScheduleOperator[],
  opColW: number,
) {
  const totalW = timeColW + opColW * operators.length;

  doc.setFillColor(...C_OP_HDR_BG);
  doc.rect(x, y, totalW, COL_HEADER_H, 'F');

  doc.setDrawColor(...C_GRID_LINE);
  doc.setLineWidth(0.25);
  doc.line(x, y + COL_HEADER_H, x + totalW, y + COL_HEADER_H);

  operators.forEach((op, i) => {
    const cellX = x + timeColW + i * opColW;
    if (i > 0) {
      doc.setDrawColor(...C_GRID_LINE);
      doc.setLineWidth(0.25);
      doc.line(cellX, y, cellX, y + COL_HEADER_H);
    }
    doc.setTextColor(...C_OP_HDR_TEXT);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    const name = `${op.first_name} ${op.last_name.toUpperCase()}`;
    doc.text(name, cellX + opColW / 2, y + COL_HEADER_H / 2 + 1.5, { align: 'center' });
  });
}

function drawTimelineGrid(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  timeColW: number,
  minHour: number, maxHour: number,
  mmPerHour: number,
) {
  doc.setFillColor(255, 255, 255);
  doc.rect(x, y, w, h, 'F');

  for (let hour = minHour; hour <= maxHour; hour++) {
    const lineY = y + (hour - minHour) * mmPerHour;

    if (hour > minHour && hour <= maxHour) {
      doc.setDrawColor(...C_GRID_LINE);
      doc.setLineWidth(0.2);
      doc.line(x + timeColW, lineY, x + w, lineY);
    }

    if (hour < maxHour) {
      doc.setTextColor(...C_TIME_LABEL);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${hour.toString().padStart(2, '0')}:00`,
        x + timeColW - 2,
        lineY + 3.2,
        { align: 'right' },
      );
    }
  }

  doc.setDrawColor(...C_GRID_LINE);
  doc.setLineWidth(0.3);
  doc.line(x + timeColW, y, x + timeColW, y + h);
}

function drawColumnSeparators(
  doc: jsPDF,
  x: number, y: number, h: number,
  timeColW: number,
  count: number,
  opColW: number,
) {
  doc.setDrawColor(...C_GRID_LINE);
  doc.setLineWidth(0.25);
  for (let i = 1; i < count; i++) {
    const sx = x + timeColW + i * opColW;
    doc.line(sx, y, sx, y + h);
  }
}

function drawTaskBlock(
  doc: jsPDF,
  bx: number, by: number, bw: number, bh: number,
  task: ScheduleTask,
) {
  const palette  = getServicePalette(task.serviceColor);
  const startMin = timeToMinutes(formatTimeShort(task.startTime));
  const timeRange = `${formatTimeShort(task.startTime)} - ${minutesToTime(startMin + task.duration)}`;
  const rx = 1.2;

  doc.setFillColor(...palette.bg);
  doc.setDrawColor(...palette.border);
  doc.setLineWidth(0.2);
  doc.roundedRect(bx, by, bw, bh, rx, rx, 'FD');

  doc.setDrawColor(...palette.border);
  doc.setLineWidth(1.6);
  doc.line(bx + 0.8, by + rx * 0.7, bx + 0.8, by + bh - rx * 0.7);

  const textX = bx + 2.8;
  const textW = Math.max(bw - 3.6, 1);

  doc.setTextColor(...palette.text);

  if (bh >= 12) {
    const titleY = by + 3.8;

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.text(task.title, textX, titleY, { maxWidth: textW });

    const titleWidth = doc.getTextWidth(task.title);
    const gap = 1.2;
    const timeX = textX + Math.min(titleWidth, textW) + gap;

    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'italic');

    if (timeX + doc.getTextWidth(timeRange) <= textX + textW) {
      doc.text(timeRange, timeX, titleY);
    } else {
      doc.text(timeRange, textX, titleY + 3.4, { maxWidth: textW });
    }

    if (task.serviceName && bh >= 15) {
      doc.setFontSize(5);
      doc.setFont('helvetica', 'normal');
      doc.text(`[${task.serviceName}]`, textX, by + 7.2, { maxWidth: textW });
    }
  } else {
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'bold');
    const midY = by + (bh + 2.2) / 2;
    doc.text(task.title, textX, midY, { maxWidth: textW });

    const titleWidth = doc.getTextWidth(task.title);
    const gap = 1.0;
    const timeX = textX + Math.min(titleWidth, textW) + gap;

    doc.setFontSize(5);
    doc.setFont('helvetica', 'italic');

    if (timeX + doc.getTextWidth(timeRange) <= textX + textW) {
      doc.text(timeRange, timeX, midY);
    }
  }
}

function drawFooter(
  doc: jsPDF,
  x: number, pageH: number, w: number,
  siteName: string, formattedDate: string,
) {
  const lineY = pageH - MARGIN - FOOTER_H + 1;
  doc.setDrawColor(...C_FOOTER_LINE);
  doc.setLineWidth(0.25);
  doc.line(x, lineY, x + w, lineY);

  doc.setTextColor(...C_FOOTER_TEXT);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text('TASQ - Planning Equipe', x, lineY + 4);
  doc.text(`${siteName} · ${formattedDate}`, x + w, lineY + 4, { align: 'right' });
}

// --- Main export ---

export async function generateTeamSchedulePdf(options: GenerateSchedulePdfOptions): Promise<void> {
  const { siteName, date, displayedOperators, tasks } = options;
  if (displayedOperators.length === 0) return;

  const logo          = await loadLogo('/logo-tasq-fh-blanc.png');
  const formattedDate = formatDateFrench(date);
  const generatedAt   = new Date().toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const allStartMins = tasks.map(t => timeToMinutes(t.startTime));
  const allEndMins   = tasks.map(t => timeToMinutes(t.startTime) + t.duration);
  const minHour      = Math.floor(Math.min(...allStartMins) / 60);
  const maxHour      = Math.ceil(Math.max(...allEndMins)   / 60);
  const hourRange    = Math.max(maxHour - minHour, 2);

  const tasksByOperator = new Map<string, ScheduleTask[]>();
  displayedOperators.forEach(op => {
    tasksByOperator.set(
      op.id,
      tasks
        .filter(t => t.operatorId === op.id)
        .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)),
    );
  });

  const doc      = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const PAGE_W   = doc.internal.pageSize.getWidth();
  const PAGE_H   = doc.internal.pageSize.getHeight();
  const USABLE_W = PAGE_W - 2 * MARGIN;

  const maxOpsPerPage = Math.max(1, Math.floor((USABLE_W - TIME_COL_W) / MIN_OP_COL_W));
  const batches: ScheduleOperator[][] = [];
  for (let i = 0; i < displayedOperators.length; i += maxOpsPerPage) {
    batches.push(displayedOperators.slice(i, i + maxOpsPerPage));
  }
  const totalPages = batches.length;

  const TIMELINE_TOP    = MARGIN + HEADER_H + META_H + COL_HEADER_H;
  const TIMELINE_BOTTOM = PAGE_H - MARGIN - FOOTER_H;
  const TIMELINE_H      = TIMELINE_BOTTOM - TIMELINE_TOP;
  const mmPerHour       = TIMELINE_H / hourRange;
  const mmPerMinute     = mmPerHour / 60;

  batches.forEach((batch, pageIndex) => {
    if (pageIndex > 0) doc.addPage('a4', 'landscape');

    const opsAreaW = USABLE_W - TIME_COL_W;
    let opColW     = opsAreaW / batch.length;
    if (batch.length === 1) opColW = Math.min(opColW, MAX_SINGLE_COL);
    const totalW   = TIME_COL_W + opColW * batch.length;

    drawHeader(doc, MARGIN, MARGIN, USABLE_W, siteName, formattedDate, logo);
    drawMetaLine(doc, MARGIN, MARGIN + HEADER_H, USABLE_W, generatedAt, pageIndex + 1, totalPages);
    drawColumnHeaders(doc, MARGIN, MARGIN + HEADER_H + META_H, TIME_COL_W, batch, opColW);

    drawTimelineGrid(
      doc,
      MARGIN, TIMELINE_TOP, totalW, TIMELINE_H,
      TIME_COL_W, minHour, maxHour, mmPerHour,
    );
    drawColumnSeparators(doc, MARGIN, TIMELINE_TOP, TIMELINE_H, TIME_COL_W, batch.length, opColW);

    batch.forEach((op, colIndex) => {
      const opTasks = tasksByOperator.get(op.id) ?? [];
      const layouts = calculateTaskLayouts(opTasks);
      const opX     = MARGIN + TIME_COL_W + colIndex * opColW;

      layouts.forEach(({ task, column, totalColumns }) => {
        const startMin    = timeToMinutes(formatTimeShort(task.startTime));
        const relativeMin = startMin - minHour * 60;
        const blockY      = TIMELINE_TOP + relativeMin * mmPerMinute;
        const rawH        = task.duration * mmPerMinute;
        const blockH      = Math.max(rawH, MIN_BLOCK_H);

        const subColW = opColW / totalColumns;
        const blockX  = opX + column * subColW + BLOCK_INSET;
        const blockW  = subColW - 2 * BLOCK_INSET;

        drawTaskBlock(doc, blockX, blockY, blockW, blockH, task);
      });
    });

    doc.setDrawColor(...C_GRID_LINE);
    doc.setLineWidth(0.3);
    doc.rect(MARGIN, MARGIN + HEADER_H + META_H, totalW, COL_HEADER_H + TIMELINE_H, 'S');

    drawFooter(doc, MARGIN, PAGE_H, USABLE_W, siteName, formattedDate);
  });

  const dateStr = date.toISOString().split('T')[0];
  const slug    = normalizeSiteSlug(siteName) || 'site';
  doc.save(`planning-equipe-${slug}-${dateStr}.pdf`);
}
